// src/views/repeat/RepeatView.tsx
import React, {
  FC, memo, useEffect, useState, useCallback, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './slice/slice';
import { Button, Spin, Result } from 'antd';
import './Repeat.less';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import { WordFromList } from '../../components/Repeat/WordFromList';
import { ReactComponent as Play } from '../../assets/icons/games/repeat/play-circle-game.svg';
import { ReactComponent as Pause } from '../../assets/icons/games/repeat/pause-circle.svg';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import { BASE_API_URL } from '../../services/ApiService';
import { notifyAudioError, watchAudioStart } from '../../utils/audioErrors';
import BackgroundLinen from '../../assets/linen.png';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { UnsplashCredits } from '../../components/UnsplashCredits/UnsplashCredits';
import { EditTermButton } from '../../components/EditTermModal/EditTermButton';
import { PolishAudioButton } from '../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../constants/Roles';
import WordCounter from '../../components/WordCounter';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

const STORAGE_KEY = 'repeat_playPolish';
const cleanTextForTTS = (text: string | undefined): string => {
  if (!text) {
    return '';
  }

  return text.replace(/\s*\(.*?\)\s*/g, ' ').trim();
};

// Przerwa po angielskim czytaniu, w której uczeń powtarza zwrot na głos: tyle, ile trwało czytanie,
// plus 1,5 s na reakcję, ale nie krócej niż 2 s.
const REPEAT_PAUSE_EXTRA = 1500; // ms
const REPEAT_PAUSE_MIN = 2000; // ms

// Co w tej chwili robi lektor: czyta polskie znaczenie, angielski zwrot albo czeka, aż uczeń powtórzy (pauseMs —
// długość przerwy). Od tego zależy, która karta jest przygaszona i czy pod angielską skraca się pasek przerwy
type Reading = { step: 'polish' | 'english' } | { step: 'pause', pauseMs: number };

// Napis na polskiej karcie. Gdy lektor czyta angielski zwrot albo trwa przerwa na powtórzenie, napis jaśnieje —
// sama karta zostaje bez zmian
const POLISH_TEXT_COLOR = '#005f5f';
const POLISH_TEXT_COLOR_QUIET = 'rgba(0, 95, 95, 0.7)';

export const RepeatView: FC = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const goToHome = useGoToRoute(Path.HOME);
  const goToNext = useGoToRoute(Path.FIND);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;
  const hasValidData = activeList && activeList.words && activeList.words.length > 0;

  useActivityTracking(ActivityGame.REPEAT);

  const [currentWords, setCurrentWords] = useState(() => {
    if (!hasValidData) return [];
    const wordsCopy = [...activeList.words];

    return wordsCopy.sort(() => 0.5 - Math.random());
  });

  const [wordOnScreen, setWordOnScreen] = useState<number>(0);
  const [listEndInfo, setListEndInfo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [reading, setReading] = useState<Reading | null>(null);

  // Toggle polskiego audio
  const [playPolish, setPlayPolish] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);

      return saved === null ? true : saved === 'true';
    } catch {
      return true;
    }
  });

  const playPolishRef = useRef(playPolish);
  useEffect(() => {
    playPolishRef.current = playPolish;
    try {
      localStorage.setItem(STORAGE_KEY, String(playPolish));
    } catch { /* ignore storage errors */ }
  }, [playPolish]);

  const togglePlayPolish = useCallback(() => {
    setPlayPolish((prev) => !prev);
  }, []);

  const polishAudioRef = useRef<HTMLAudioElement | null>(null);
  const englishAudioRef = useRef<HTMLAudioElement | null>(null);
  const nextWordTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isPausedRef = useRef<boolean>(false);
  // Czy gra grała, gdy nauczyciel otworzył „Edytuj zwrot” — wtedy po zamknięciu okna rusza sama
  const resumeAfterEditRef = useRef<boolean>(false);

  const cleanupAll = useCallback(() => {
    if (nextWordTimeoutRef.current) {
      clearTimeout(nextWordTimeoutRef.current);
      nextWordTimeoutRef.current = null;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }

    if (polishAudioRef.current) {
      polishAudioRef.current.pause();
      polishAudioRef.current.currentTime = 0;
      polishAudioRef.current = null;
    }

    if (englishAudioRef.current) {
      englishAudioRef.current.pause();
      englishAudioRef.current.currentTime = 0;
      englishAudioRef.current = null;
    }
  }, []);

  const repeatGame = useCallback(() => {
    if (!hasValidData) return;

    cleanupAll();
    isPausedRef.current = false;

    setTimeout(() => {
      const wordsCopy = [...activeList.words];
      setCurrentWords(wordsCopy.sort(() => 0.5 - Math.random()));
      setWordOnScreen(0);
      setListEndInfo(false);
      setIsPlaying(true);
    }, 800);
  }, [hasValidData, activeList, cleanupAll]);

  const moveToNextWord = useCallback(() => {
    if (isPausedRef.current) {
      console.log('[moveToNextWord] Blocked - game is paused');

      return;
    }

    console.log('[moveToNextWord] Moving from', wordOnScreen, 'to', wordOnScreen + 1);

    setWordOnScreen((prevWord) => {
      const numberOfWords = currentWords.length - 1;

      if (prevWord < numberOfWords) {
        return prevWord + 1;
      }

      setListEndInfo(true);
      setIsPlaying(false);
      isPausedRef.current = false;

      return prevWord;
    });
  }, [currentWords.length, wordOnScreen]);

  const togglePlayPause = useCallback(() => {
    if (isProcessing) return;

    setIsProcessing(true);

    // Wyczyść wszystkie timeouty
    if (nextWordTimeoutRef.current) {
      clearTimeout(nextWordTimeoutRef.current);
      nextWordTimeoutRef.current = null;
    }

    const willBePlaying = !isPlaying;

    // Ustawiamy isPausedRef PRZED pauzowaniem audio
    isPausedRef.current = !willBePlaying;

    if (!willBePlaying) {
      if (polishAudioRef.current) {
        polishAudioRef.current.pause();
      }
      if (englishAudioRef.current) {
        englishAudioRef.current.pause();
      }
    }

    if (willBePlaying && listEndInfo) {
      setListEndInfo(false);
    }

    setIsPlaying(willBePlaying);

    debounceTimeoutRef.current = setTimeout(() => {
      setIsProcessing(false);
    }, 200);
  }, [isProcessing, isPlaying, listEndInfo]);

  // Okno „Edytuj zwrot” zatrzymuje grę jak PAUSE, a po zamknięciu przywraca stan sprzed otwarcia:
  // grała — czyta bieżące słowo od początku, stała — stoi dalej
  const handleEditTermModalVisibleChange = useCallback((visible: boolean) => {
    if (visible) {
      resumeAfterEditRef.current = isPlaying;
      if (!isPlaying) return;

      if (nextWordTimeoutRef.current) {
        clearTimeout(nextWordTimeoutRef.current);
        nextWordTimeoutRef.current = null;
      }
      isPausedRef.current = true;
      if (polishAudioRef.current) {
        polishAudioRef.current.pause();
      }
      if (englishAudioRef.current) {
        englishAudioRef.current.pause();
      }
      setIsPlaying(false);

      return;
    }

    if (resumeAfterEditRef.current) {
      resumeAfterEditRef.current = false;
      isPausedRef.current = false;
      setIsPlaying(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    setIsLoading(false);
  }, [hasValidData]);

  // Zatrzymana gra (PAUSE, okno „Edytuj zwrot”, koniec listy) zapomina, co czytał lektor — po wznowieniu czyta słowo
  // od początku i sam to ustawia, a do tej chwili obie karty są w pełni widoczne
  useEffect(() => {
    if (!isPlaying) setReading(null);
  }, [isPlaying]);

  useEffect(() => {
    if (!hasValidData || !isPlaying || currentWords.length === 0) {
      return () => {};
    }

    if (!currentWords[wordOnScreen]) {
      setIsPlaying(false);

      return () => {};
    }

    // NIE nadpisuj isPausedRef - sprawdź czy już nie jesteśmy w pauzie
    if (isPausedRef.current) {
      return () => {};
    }

    const currentWord = currentWords[wordOnScreen];
    const { term, definition } = currentWord;

    // Wyczyść tekst przed walidacją i enkodowaniem
    const cleanTerm = cleanTextForTTS(term);
    const cleanDefinition = cleanTextForTTS(definition);

    // Helper do czyszczenia timeoutów
    const clearPendingTimeouts = () => {
      if (nextWordTimeoutRef.current) {
        clearTimeout(nextWordTimeoutRef.current);
        nextWordTimeoutRef.current = null;
      }
    };

    const handleError = (error: Event) => {
      const audioElement = error.target as HTMLAudioElement;

      if (audioElement?.error?.code === 4) {
        return;
      }

      if (audioElement?.error) {
        console.error('Audio error:', audioElement.error);
        if (!isPausedRef.current) {
          clearPendingTimeouts();
          nextWordTimeoutRef.current = setTimeout(() => {
            if (!isPausedRef.current) {
              moveToNextWord();
            }
          }, 1000);
        }
      }
    };

    // Lektor nie zaczął mówić w czasie (np. serwer nie odpowiada) – jak przy błędzie: następne słowo
    const handleStartTimeout = () => {
      notifyAudioError();
      if (!isPausedRef.current) {
        clearPendingTimeouts();
        nextWordTimeoutRef.current = setTimeout(() => {
          if (!isPausedRef.current) {
            moveToNextWord();
          }
        }, 1000);
      }
    };

    const handlePolishEnded = () => {
      if (isPausedRef.current) {
        return;
      }

      setReading({ step: 'english' });

      // Zawsze odtwórz angielskie audio po polskim (jeśli polskie było włączone)
      if (englishAudioRef.current) {
        const playPromise = englishAudioRef.current.play();
        if (playPromise !== undefined) {
          watchAudioStart(englishAudioRef.current, playPromise, handleStartTimeout);
          playPromise.catch((error) => {
            if (error.name !== 'AbortError') {
              console.error('Error playing English audio:', error);
              notifyAudioError(error);
              if (!isPausedRef.current) {
                clearPendingTimeouts();
                nextWordTimeoutRef.current = setTimeout(() => {
                  if (!isPausedRef.current) {
                    moveToNextWord();
                  }
                }, 1000);
              }
            }
          });
        }
      }
    };

    const handleEnglishEnded = (event: Event) => {
      if (isPausedRef.current) {
        return;
      }

      // Na końcu nagrania currentTime (w sekundach) to czas czytania
      const readingTime = (event.target as HTMLAudioElement).currentTime * 1000; // ms
      const repeatPause = Math.max(REPEAT_PAUSE_MIN, readingTime + REPEAT_PAUSE_EXTRA);

      setReading({ step: 'pause', pauseMs: repeatPause });

      clearPendingTimeouts();
      nextWordTimeoutRef.current = setTimeout(() => {
        if (!isPausedRef.current) {
          // Słowo odsłuchane do końca, razem z przerwą na powtórzenie — w monitoringu aktywności próba bez wyniku,
          // która liczy się tylko do czasu pracy. Słowo pominięte po błędzie lektora próbą nie jest
          recordAnswer(null, term);
          moveToNextWord();
        }
      }, repeatPause);
    };

    // Zaktualizuj walidację, aby używała CZYSTYCH danych
    if (!cleanTerm || cleanTerm.trim() === '' || !cleanDefinition || cleanDefinition.trim() === '') {
      console.error('Empty or invalid term/definition after cleaning at index', wordOnScreen, 'word:', currentWord);
      if (!isPausedRef.current) {
        clearPendingTimeouts();
        nextWordTimeoutRef.current = setTimeout(() => {
          if (!isPausedRef.current) {
            moveToNextWord();
          }
        }, 1000);
      }

      return () => {};
    }

    // Użyj CZYSTYCH danych do enkodowania
    const encodedTerm = encodeURIComponent(cleanTerm);
    const encodedDefinition = encodeURIComponent(cleanDefinition);

    // Tworzenie audio dla angielskiego słowa (zawsze)
    const englishAudioUrl = `${BASE_API_URL}/v1/tts?q=${encodedTerm}`;
    const newEnglishAudio = new Audio(englishAudioUrl);
    englishAudioRef.current = newEnglishAudio;
    newEnglishAudio.addEventListener('ended', handleEnglishEnded);
    newEnglishAudio.addEventListener('error', handleError);

    // Sprawdź czy polskie audio jest włączone
    if (playPolishRef.current) {
      // Polskie audio WŁĄCZONE - odtwórz polskie -> angielskie
      setReading({ step: 'polish' });
      const polishAudioUrl = `${BASE_API_URL}/v1/tts?q=${encodedDefinition}&lang=pl`;
      const newPolishAudio = new Audio(polishAudioUrl);
      polishAudioRef.current = newPolishAudio;
      newPolishAudio.addEventListener('ended', handlePolishEnded);
      newPolishAudio.addEventListener('error', handleError);

      const playPromise = newPolishAudio.play();
      if (playPromise !== undefined) {
        watchAudioStart(newPolishAudio, playPromise, handleStartTimeout);
        playPromise.catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Error playing Polish audio:', error);
            notifyAudioError(error);
            if (!isPausedRef.current) {
              clearPendingTimeouts();
              nextWordTimeoutRef.current = setTimeout(() => {
                if (!isPausedRef.current) {
                  moveToNextWord();
                }
              }, 1000);
            }
          }
        });
      }
    } else {
      // Polskie audio WYŁĄCZONE - odtwórz tylko angielskie
      setReading({ step: 'english' });
      const playPromise = newEnglishAudio.play();
      if (playPromise !== undefined) {
        watchAudioStart(newEnglishAudio, playPromise, handleStartTimeout);
        playPromise.catch((error) => {
          if (error.name !== 'AbortError') {
            console.error('Error playing English audio:', error);
            notifyAudioError(error);
            if (!isPausedRef.current) {
              clearPendingTimeouts();
              nextWordTimeoutRef.current = setTimeout(() => {
                if (!isPausedRef.current) {
                  moveToNextWord();
                }
              }, 1000);
            }
          }
        });
      }
    }

    return () => {
      clearPendingTimeouts();
      if (polishAudioRef.current) {
        polishAudioRef.current.pause();
        polishAudioRef.current.removeEventListener('ended', handlePolishEnded);
        polishAudioRef.current.removeEventListener('error', handleError);
        polishAudioRef.current.src = '';
        polishAudioRef.current = null;
      }
      if (englishAudioRef.current) {
        englishAudioRef.current.pause();
        englishAudioRef.current.removeEventListener('ended', handleEnglishEnded);
        englishAudioRef.current.removeEventListener('error', handleError);
        englishAudioRef.current.src = '';
        englishAudioRef.current = null;
      }
    };
  }, [isPlaying, wordOnScreen, currentWords, hasValidData, moveToNextWord]);

  useEffect(() => () => {
    cleanupAll();
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    isPausedRef.current = false;
  }, [cleanupAll]);

  if (isLoading) {
    return (
      <FixWidthContent>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '400px',
        }}
        >
          <Spin size="large" tip={t('games:states.loadingList')} />
        </div>
      </FixWidthContent>
    );
  }

  if (!hasValidData || currentWords.length === 0) {
    return (
      <FixWidthContent>
        <Result
          status="warning"
          title={t('games:states.noActiveList')}
          subTitle={t('games:states.chooseList')}
          extra={(
            <Button type="primary" onClick={goToHome}>
              {t('games:states.backToLists')}
            </Button>
          )}
        />
      </FixWidthContent>
    );
  }

  const currentWord = currentWords[wordOnScreen];
  // Lektor czyta tylko w trakcie gry — zatrzymana gra ma obie karty w pełni widoczne
  const readingNow = isPlaying ? reading : null;
  if (!listEndInfo && !currentWord) {
    return (
      <FixWidthContent>
        <Result
          status="error"
          title={t('games:states.dataError')}
          subTitle={t('games:states.wordLoadError')}
          extra={(
            <Button type="primary" onClick={goToHome}>
              {t('games:states.backToLists')}
            </Button>
          )}
        />
      </FixWidthContent>
    );
  }

  return (
    <>
      <FixWidthContent className="repeat-main">
        {!listEndInfo && (
          <WordCounter
            current={wordOnScreen + 1}
            total={currentWords.length}
          />
        )}
        {/* Na telefonie trzymanym poziomo obrazek, karty i GO stoją w jednym rzędzie (Repeat.less) */}
        {!listEndInfo && currentWord && (
          <div className="repeat-stage">
            <div className="pict">
              {currentWord.image && (
                <img
                  src={currentWord.image}
                  alt={currentWord.term}
                />
              )}
            </div>

            {/* Polska karta, gdy lektor jej nie czyta, ma jaśniejszy napis, a angielska jest przygaszona cała;
                w przerwie na powtórzenie pod angielską skraca się pasek (Repeat.less) */}
            <div className="repeat-cards">
              <div className="repeat-card">
                <WordFromList
                  id={currentWord.id}
                  content={currentWord.definition || ''}
                  style={{
                    background: `url(${BackgroundLinen})`,
                    color: readingNow && readingNow.step !== 'polish' ? POLISH_TEXT_COLOR_QUIET : POLISH_TEXT_COLOR,
                  }}
                />
              </div>
              <div className={`repeat-card ${readingNow?.step === 'polish' ? 'repeat-card-dimmed' : ''}`}>
                {/* Angielski zwrot, który uczeń powtarza, ciemnym grafitem. Dotąd stało tu 'verydarkgrey' — nazwa,
                    której przeglądarka nie zna, więc karta miała domyślny, blady kolor .word */}
                <WordFromList
                  id={currentWord.id}
                  content={currentWord.term || ''}
                  style={{ color: '#333333' }}
                />
                {/* Żłobek paska przerwy widać cały czas, wstęgę w nim — tylko w przerwie na powtórzenie */}
                <div className="repeat-pause-bar">
                  {readingNow?.step === 'pause' && (
                    <div className="repeat-pause-bar-fill" style={{ animationDuration: `${readingNow.pauseMs}ms` }} />
                  )}
                </div>
              </div>
            </div>

            <div className="control_panel">
              <Button
                id="play_button"
                onClick={togglePlayPause}
                disabled={isProcessing}
              >
                {!isPlaying ? <Play className="btnLabel" /> : <Pause className="btnLabel_active" />}
                <br />
                {!isPlaying ? t('games:buttons.go') : t('games:buttons.pause')}
              </Button>
            </div>
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} firstRound />
        )}

        {!listEndInfo && <UnsplashCredits words={currentWord ? [currentWord] : []} />}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && currentWord && (
            <EditTermButton
              wordId={currentWord.id}
              currentTerm={currentWord.term}
              currentDefinition={currentWord.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                setCurrentWords((prev) => prev.map((word) => (word.id === currentWord.id
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
              onModalVisibleChange={handleEditTermModalVisibleChange}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}

        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(RepeatView);
